import { format, fromUnixTime } from "date-fns";
import { enUS, hu } from "date-fns/locale";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { ApiError, Congress } from "@/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import UploadInput from "@/features/file-upload/components/upload-input";
import { queryClient } from "@/libs/react-query";
import { cn } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { useCreateCongress } from "../api/create-congress";
import { congressMetaQueryKey } from "../api/get-congress";
import { useUpdateCongress } from "../api/update-congress";
import {
  CongressDetailsFormData,
  congressDetailsSchema,
} from "../types/congress-details-schema";
import { TimezoneSelector } from "./timezone-selector";

type CongressDetailsFormProps = {
  onSuccess?: () => void;
  initialData?: Congress;
};

function transformApiToFormData(congress?: Congress): Partial<CongressDetailsFormData> {
  if (!congress) {
    return {
      name: "",
      date: {
        from: new Date(),
        to: new Date(),
      },
      coverImage: {
        id: "",
        url: "",
      },
      textColor: "",
      backgroundColor: "",
      status: false,
      isHidden: true,
      source: "",
      timezone: "",
    };
  }

  return {
    congId: congress.congId,
    name: congress.name,
    date: {
      from: new Date(fromUnixTime(congress.from)),
      to: new Date(fromUnixTime(congress.to)),
    },
    coverImage: {
      id: congress.cover?.id ?? "",
      url: congress.cover?.file_name ?? "",
    },
    textColor: congress.textColor ?? "",
    backgroundColor: congress.color ?? "",
    status: congress.status,
    isHidden: congress.hidden,
    source: congress.source ?? "",
    timezone: congress.timezone ?? "",
  };
}

export function CongressDetailsForm(props: CongressDetailsFormProps) {
  const { t, i18n } = useTranslation();
  const { initialData } = props;
  const form = useForm<CongressDetailsFormData>({
    resolver: zodResolver(congressDetailsSchema),
    defaultValues: transformApiToFormData(initialData),
  });

  const { mutateAsync: createCongress, isSuccess: isCreateSuccess } =
    useCreateCongress();
  const { mutateAsync: updateCongress, isSuccess: isUpdateSuccess } =
    useUpdateCongress(initialData ? initialData.id : "");
  const {
    setCongress,
    congress: { id: congressId },
  } = useCongress();
  const navigate = useNavigate();

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      queryClient.invalidateQueries({
        queryKey: congressMetaQueryKey(congressId),
      });
    }
  }, [isCreateSuccess, isUpdateSuccess, congressId]);

  const isEdit = !!initialData;

  const handleUpdateCongress = async (data: CongressDetailsFormData) => {
    try {
      const congress = await updateCongress({
        congId: data.congId,
        from: data.date.from.toISOString(),
        to: data.date.to.toISOString(),
        hidden: data.isHidden,
        color: data.backgroundColor,
        name: data.name,
        status: data.status,
        textColor: data.textColor,
        source: data.source,
        coverId: data.coverImage.id,
        timezone: data.timezone,
      });
      form.reset(transformApiToFormData(congress));
    } catch (error) {
      if (error instanceof ApiError) {
        form.setError("root", {
          type: "manual",
          // @ts-expect-error body is not typed
          message: Array.isArray(error.body?.message)
            ? // @ts-expect-error body is not typed
              error.body?.message.join(", ")
            : // @ts-expect-error body is not typed
              error.body?.message,
        });
      }
    }
  };

  const handleCreateCongress = async (data: CongressDetailsFormData) => {
    try {
      const congress = await createCongress({
        requestBody: {
          congId: data.congId,
          from: data.date.from.toISOString(),
          to: data.date.to.toISOString(),
          hidden: data.isHidden,
          color: data.backgroundColor,
          name: data.name,
          status: data.status,
          textColor: data.textColor,
          source: data.source,
          coverId: data.coverImage.id,
          timezone: data.timezone,
        },
      });

      setCongress(congress);
      if (props.onSuccess) {
        props.onSuccess();
      }
      navigate("/");
    } catch (error) {
      if (error instanceof ApiError) {
        form.setError("root", {
          type: "manual",
          // @ts-expect-error body is not typed
          message: error.body?.message,
        });
      }
    }
  };

  const textColor = form.watch("textColor");
  const backgroundColor = form.watch("backgroundColor");

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          isEdit ? handleUpdateCongress : handleCreateCongress,
        )}
      >
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="congId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("congressForm.congressId")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("congressForm.exampleId")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("congressForm.name")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("congressForm.name")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("congressForm.date")}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-[240px] pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? (
                          field.value.to ? (
                            <>
                              {format(field.value.from, "yyyy.MM.dd")} -{" "}
                              {format(field.value.to, "yyyy.MM.dd")}
                            </>
                          ) : (
                            format(field.value.from, "yyyy.MM.dd")
                          )
                        ) : (
                          <span>{t("congressForm.pickDate")}</span>
                        )}
                        <CalendarIcon className="ml-auto size-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={field.value}
                      onSelect={field.onChange}
                      numberOfMonths={2}
                      locale={i18n.resolvedLanguage === "hu" ? hu : enUS}
                      defaultMonth={field.value?.from}
                    />
                  </PopoverContent>
                </Popover>
                <FormField
                  control={form.control}
                  name="date.to"
                  render={() => (
                    <FormItem>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="date.from"
                  render={() => (
                    <FormItem>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("congressForm.timezone")}</FormLabel>
                <FormControl>
                  <TimezoneSelector
                    value={field.value}
                    onChange={field.onChange}
                    disabled={form.formState.isSubmitting}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription className="text-xs">
                  {t("congressForm.deviceTimezone")}{" "}
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </FormDescription>
              </FormItem>
            )}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="backgroundColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("congressForm.backgroundColor")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("congressForm.backgroundColor")}
                      className="h-8 px-2 py-1"
                      disabled={form.formState.isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t("congressForm.colorFormat")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="textColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("congressForm.textColor")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("congressForm.textColor")}
                      className="h-8 px-2 py-1"
                      disabled={form.formState.isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div
            style={{ backgroundColor }}
            className="rounded-lg border border-border p-4"
          >
            <p style={{ color: textColor }}>{t("congressForm.preview")}</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("congressForm.active")}
                    </FormLabel>
                    <FormDescription>{t("congressForm.activeDescription")}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isHidden"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("congressForm.hidden")}
                    </FormLabel>
                    <FormDescription>
                      {t("congressForm.hiddenDescription")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="source"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("congressForm.sourceUrl")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("congressForm.sourceUrl")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="coverImage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("congressForm.coverImage")}</FormLabel>
                <FormControl>
                  <UploadInput
                    isSubmitting={form.formState.isSubmitting}
                    onChange={field.onChange}
                    value={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{t("common.errorTitle")}</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div
          className={cn(
            "mt-6 flex sm:justify-between",
            isEdit && "sm:justify-end",
          )}
        >
          {!isEdit && (
            <Button type="button" variant="secondary" onClick={props.onSuccess}>
              {t("congressForm.cancel")}
            </Button>
          )}
          <Button
            className="mb-4 sm:mb-0"
            type="submit"
            disabled={!form.formState.isDirty}
          >
            {isEdit
              ? form.formState.isDirty
                ? t("congressForm.save")
                : t("congressForm.saved")
              : t("congressForm.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
